import CloseIcon from '@/icons/close';
import FavoriteIcon from '@/icons/favorite';
import FavoriteBorderIcon from '@/icons/favorite-border';
import { Dialog, Transition } from '@headlessui/react';
import React, {
  Fragment,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';
import clsx from 'clsx';
import Spinner from '@/icons/spinner';
import { useAuth } from '@/context/auth';
import {
  fetchFavoritePropertyUserWise,
  updateFavoriteByIdForUser,
  useFavorite,
} from '@/context/favorite/hooks/use-favorite';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '@/types/store';
import { FavoriteState } from '@/context/favorite/types';
import { favoriteActions } from '@/context/favorite/slice';
import { sentryCaptureException } from '@/utility';

interface User {
  name: string;
  email: string;
  isFavorite: boolean;
}

interface IconButtonProps {
  onClick?: (event: React.MouseEvent) => void;
  color?: 'primary' | 'danger';
  disabled?: boolean;
}

function IconButton(props: PropsWithChildren<IconButtonProps>) {
  return (
    <button
      disabled={props.disabled}
      onClick={props.onClick}
      className={clsx(
        'rounded-full p-2 hover:bg-gray-100 disabled:hover:bg-transparent text-primary-500 disabled:text-gray-300'
      )}
    >
      {props.children}
    </button>
  );
}

interface ListItemProps {
  user: User;
  noBorder?: boolean;
  onToggle?: () => Promise<void>;
}

const ListItem: React.FC<ListItemProps> = (props) => {
  const [updating, setUpdating] = useState(false);
  const name = useMemo(() => {
    const firstName = props.user.name?.split(' ')[0];
    const lastName = props.user.name?.split(' ')[1];

    if (!lastName) return firstName;
    return `${firstName} ${lastName[0]}`;
  }, [props.user.name]);

  const handleToggle = async () => {
    setUpdating(true);
    if (props.onToggle) {
      await props.onToggle();
    }
    setUpdating(false);
  };

  return (
    <div
      className={clsx(
        'flex flex-row items-center py-2 px-6 w-full',
        !props.noBorder && 'border-t-2'
      )}
    >
      <div className="flex flex-col flex-1 items-start">
        <div className="text-gray-600">
          <span>{name}</span>
        </div>
        {/* <div className="text-gray-500">
          <span>{props.user.email}</span>
        </div> */}
      </div>
      <div className="flex gap-3">
        <IconButton onClick={handleToggle} disabled={updating}>
          {props.user.isFavorite && <FavoriteIcon width={24} height={24} />}
          {!props.user.isFavorite && (
            <FavoriteBorderIcon width={24} height={24} />
          )}
        </IconButton>
      </div>
    </div>
  );
};

export const FavoriteUserListModal = () => {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const { authInfo } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const favState = useSelector<AppState, FavoriteState>(
    (state) => state.favorite
  );
  const open = Boolean(favState.popupPropertyNumber);
  const isFavorite = !!favState.favorites.find(
    (x) => x.propertyNumber === favState.popupPropertyNumber
  );
  const dispatch = useDispatch();
  const { toggleFavorite } = useFavorite();

  useEffect(() => {
    if (!favState.popupPropertyNumber) return;
    setLoading(true);
    setMessage('');
    setUsers([]);
    fetchFavoritePropertyUserWise(favState.popupPropertyNumber).then((res) => {
      setUsers(
        res.map((x) => ({
          email: x.email,
          name: x.name,
          isFavorite: x.isFavorite,
        }))
      );
      setLoading(false);
    });
  }, [favState.popupPropertyNumber]);

  const handleClose = () => {
    dispatch(favoriteActions.setPopupPropertyNumber(null));
  };

  const handleChange = async (customerId: string, value: boolean) => {
    try {
      if (customerId === authInfo.email) {
        await toggleFavorite(favState.popupPropertyNumber, message);
        return;
      }

      const user = users.find((x) => x.email === customerId);
      await updateFavoriteByIdForUser(
        customerId,
        favState.popupPropertyNumber,
        value,
        message
      );
      if (user) {
        user.isFavorite = value;
      }
      setUsers([...users]);
    } catch (error) {
      sentryCaptureException(error);
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={handleClose}
      >
        <div className="min-h-screen sm:px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full max-w-xl sm:my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl sm:rounded-2xl ">
              <Dialog.Title
                as="h3"
                className="text-lg font-semibold leading-6 text-gray-900 bg-gray-50 px-6 py-4 flex items-center justify-between"
              >
                <span>Manage Favourite</span>
                <button onClick={handleClose}>
                  <CloseIcon className="h-5 w-5" />
                </button>
              </Dialog.Title>
              <div className="py-5 overflow-auto">
                <div className="px-2 pb-2">
                  <textarea
                    className="border w-full py-2 px-4 rounded outline-none"
                    style={{ resize: 'none' }}
                    placeholder="Message"
                    required
                    name="message"
                    value={message}
                    maxLength={500}
                    autoComplete="off"
                    rows={2}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>
                <ListItem
                  noBorder
                  user={{
                    email: authInfo?.email,
                    name: authInfo?.name,
                    isFavorite: isFavorite,
                  }}
                  onToggle={() => handleChange(authInfo?.email, !isFavorite)}
                />
                {users.map((user, index) => (
                  <ListItem
                    key={index + user.name}
                    user={user}
                    onToggle={() => handleChange(user.email, !user.isFavorite)}
                  />
                ))}
                {loading && (
                  <div className="flex items-center justify-center h-8">
                    <Spinner className="h-6 w-6 text-primary-500 spin" />
                  </div>
                )}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
